import { LIST_BIGQUERY_NAME } from "@/lib/ai/tools/list-bigquery/constants";
import type { ListBigqueryOutput } from "@/lib/ai/tools/list-bigquery/server";
import { defineDatasetSpec } from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  buildListTableDataset,
  createTableOnlyProfile,
  toListText,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const BIGQUERY_DATASET_SPEC = defineDatasetSpec({
  id: `${LIST_BIGQUERY_NAME}-table`,
  profile: createTableOnlyProfile({ datasetFamily: "list.bigquery" }),
  fields: [
    { key: "analyticsType", role: "dim", type: "string" },
    { key: "organizationId", role: "dim", type: "string" },
    { key: "bigqueryId", role: "dim", type: "string" },
    { key: "projectId", role: "dim", type: "string" },
    { key: "datasetName", role: "dim", type: "string" },
    { key: "manageType", role: "dim", type: "string" },
    { key: "latestDataDate", role: "dim", type: "string" },
    { key: "earliestDataDate", role: "dim", type: "string" },
    { key: "updatedAt", role: "dim", type: "string" },
  ] as const,
  defaults: { category: "analyticsType" },
  notes: "Table dataset normalized from list_bigquery output.",
});

function toBigqueryRows(output: Partial<ListBigqueryOutput>): VizDatasetRow[] {
  const saRows = Array.isArray(output.searchAnalyticsBigQueries)
    ? output.searchAnalyticsBigQueries.map((row) => ({
        analyticsType: "search_analytics",
        organizationId: toListText(output.organizationId),
        bigqueryId: toListText(row.id),
        projectId: toListText(row.projectID),
        datasetName: toListText(row.datasetName),
        manageType: toListText(row.manageType),
        latestDataDate: toListText(row.latestDataDate),
        earliestDataDate: null,
        updatedAt: null,
      }))
    : [];

  const uaRows = Array.isArray(output.userAnalyticsBigQueries)
    ? output.userAnalyticsBigQueries.map((row) => ({
        analyticsType: "user_analytics",
        organizationId: toListText(output.organizationId),
        bigqueryId: toListText(row.id),
        projectId: toListText(row.projectID),
        datasetName: toListText(row.datasetName),
        manageType: null,
        latestDataDate: toListText(row.latestDataDate),
        earliestDataDate: toListText(row.earliestDataDate),
        updatedAt: toListText(row.updatedAt),
      }))
    : [];

  return [...saRows, ...uaRows];
}

/**
 * list_bigquery 出力を table-only dataset へ正規化する。
 */
export function buildListBigqueryDatasets(
  output: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const typedOutput =
    typeof output === "object" && output !== null
      ? (output as Partial<ListBigqueryOutput>)
      : {};
  const rows = toBigqueryRows(typedOutput);
  const dataset = buildListTableDataset({
    id: BIGQUERY_DATASET_SPEC.id,
    datasetFamily: BIGQUERY_DATASET_SPEC.profile.datasetFamily,
    variant: BIGQUERY_DATASET_SPEC.profile.variant,
    source,
    rows,
    fields: BIGQUERY_DATASET_SPEC.fields,
    defaults: BIGQUERY_DATASET_SPEC.defaults,
    notes: BIGQUERY_DATASET_SPEC.notes,
  });

  return dataset ? [dataset] : [];
}
